import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { getApiErrorStatus } from "_shared/_errors/api-error-message";
import { api, getApiEndpointName } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import Guid from "_shared/guids/guid";
import AuthInfoSchema from "users/auth/info/auth-info-schema";
import { AuthInfo } from "users/auth/info/auth-info";
import type { AuthResponse } from "users/_api/auth-email-api-service";
import SearchUsersAPIRequest from "users/super-users/_api/commands/search-users-api-request";
import SystemNotificationAPIResponse from "users/super-users/_api/responses/system-notification-api-response";
import UserEntityMemberInvitation from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import UserEntityMemberInvitationSchema, {
  UserEntityMemberInvitationJSON,
} from "legal-entities/invitations/member-invitations/email-based/_schema/user-entity-member-invitation-schema";
import SystemNotification from "notifications/system-notifications/system-notification";
import {
  parseSystemMessageSeverity,
  SystemMessageSeverity,
} from "notifications/system-notifications/system-message-severity";
import UserInformationSchema, { UserInformationJSON } from "users/information/_schemas/user-information-schema";
import UserInformation from "users/information/user-information";
import { HttpStatusCode } from "axios";
import z from "zod";

const superUserListTag = { type: "UserInformation" as const, id: "SUPER_USER_LIST" };
const systemNotificationsTag = { type: "SystemNotification" as const, id: "LIST" };
const activeInvitationsTag = { type: "AdministratorInvitation" as const, id: "ACTIVE_USER_LIST" };
const systemNotificationResponseSchema = z.object({
  id: z.uuid(),
  content: z.string(),
  message: z.string().optional(),
  severity: z.string().optional(),
  createdDate: z.string().optional(),
});
type SystemNotificationResponseJSON = z.infer<typeof systemNotificationResponseSchema>;
export type OnboardingWorkStaffResolutionResponse = {
  requestId: string;
  state: string;
  currentProposalId: string;
  currentNegotiationId: string;
  relatedProposalIds: string[];
  closedByUserId?: string | null;
  closedDate?: string | null;
  closureReason?: string | null;
};
const onboardingWorkStaffResolutionResponseSchema: z.ZodType<OnboardingWorkStaffResolutionResponse> = z.object({
  requestId: z.uuid(),
  state: z.string().min(1),
  currentProposalId: z.uuid(),
  currentNegotiationId: z.uuid(),
  relatedProposalIds: z.array(z.uuid()),
  closedByUserId: z.uuid().nullish(),
  closedDate: z.string().nullish(),
  closureReason: z.string().nullish(),
});

export const superUserApi = api.injectEndpoints({
  endpoints: (build) => ({
    registerSuperUser: build.mutation<void, { userId: Guid }>({
      query: ({ userId }) => ({
        url: "super-user/register",
        method: "POST",
        body: JSON.stringify({ userId: userId.value }),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [superUserListTag],
    }),
    unregisterSuperUser: build.mutation<void, { userId: Guid }>({
      query: ({ userId }) => ({
        url: "super-user/unregister",
        method: "POST",
        body: JSON.stringify({ userId: userId.value }),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [superUserListTag],
    }),
    replaceOnboardingProposal: build.mutation<OnboardingWorkStaffResolutionResponse, { requestId: string; templateId: string }>({
      query: ({ requestId, templateId }) => ({
        url: `work/onboarding/${requestId}/replace-proposal`,
        method: "POST",
        body: JSON.stringify({ templateId }),
        headers: { "Content-Type": "application/json" },
      }),
      rawResponseSchema: onboardingWorkStaffResolutionResponseSchema,
      onSchemaFailure: logSchemaFailure,
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [superUserListTag],
    }),
    closeOnboarding: build.mutation<OnboardingWorkStaffResolutionResponse, { requestId: string; reason: string }>({
      query: ({ requestId, reason }) => ({
        url: `work/onboarding/${requestId}/close`,
        method: "POST",
        body: JSON.stringify({ reason }),
        headers: { "Content-Type": "application/json" },
      }),
      rawResponseSchema: onboardingWorkStaffResolutionResponseSchema,
      onSchemaFailure: logSchemaFailure,
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [superUserListTag],
    }),
    disableSuperUser: build.mutation<void, { userId: Guid }>({
      query: ({ userId }) => ({
        url: "super-user/disable-user",
        method: "POST",
        body: JSON.stringify({ userId: userId.value }),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [superUserListTag],
    }),
    enableSuperUser: build.mutation<void, { userId: Guid }>({
      query: ({ userId }) => ({
        url: "super-user/enable-user",
        method: "POST",
        body: JSON.stringify({ userId: userId.value }),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [superUserListTag],
    }),
    loginAsUser: build.mutation<AuthResponse, { userId: Guid }>({
      query: ({ userId }) => ({
        url: "super-user/login-as-user",
        method: "POST",
        body: JSON.stringify({ userId: userId.value }),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: AuthInfoSchema.AuthInfoJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown) => AuthInfo.parseAuthResponse(response),
      transformErrorResponse: toSuperUserApiError,
    }),
    createSystemNotification: build.mutation<
      void,
      { notificationType: SystemMessageSeverity; notificationContent: string }
    >({
      query: ({ notificationType, notificationContent }) => ({
        url: "super-user/system-notifications",
        method: "POST",
        body: JSON.stringify({ message: notificationContent, severity: notificationType }),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [systemNotificationsTag],
    }),
    updateSystemNotification: build.mutation<
      void,
      { notificationId: Guid; notificationType: SystemMessageSeverity; notificationContent: string }
    >({
      query: ({ notificationId, notificationType, notificationContent }) => ({
        url: `super-user/system-notifications/${notificationId.value}`,
        method: "PUT",
        body: JSON.stringify({ message: notificationContent, severity: notificationType }),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [systemNotificationsTag],
    }),
    deleteSystemNotification: build.mutation<void, { notificationId: Guid }>({
      query: ({ notificationId }) => ({ url: `super-user/system-notifications/${notificationId.value}`, method: "DELETE" }),
      transformErrorResponse: toSuperUserApiError,
      invalidatesTags: [systemNotificationsTag],
    }),
    searchUsers: build.query<PaginatedResponse<UserInformation>, { request: SearchUsersAPIRequest }>({
      query: ({ request }) => ({ url: "super-user/search-users", params: searchUsersParams(request) }),
      rawResponseSchema: z.array(UserInformationSchema.UserInformationJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: UserInformationJSON[], meta) =>
        PaginatedResponse.from(
          response.map((item) => UserInformation.fromJSON(item)).filter((item): item is UserInformation => Boolean(item)),
          meta?.response?.headers.get("x-pagination") ?? "",
        ),
      transformErrorResponse: toSuperUserApiError,
      providesTags: [superUserListTag],
    }),
    getAppVersion: build.query<string, void>({
      query: () => "super-user/version",
      rawResponseSchema: z.string(),
      onSchemaFailure: logSchemaFailure,
      transformErrorResponse: (response, meta) => {
        if (getApiErrorStatus(response) === HttpStatusCode.Forbidden) {
          return new VersionInfoNotAvailableError("Unable to fetch version info, caller must be a super-user");
        }
        return toSuperUserApiError(response, meta);
      },
    }),
    getSystemNotifications: build.query<SystemNotification[], void>({
      query: () => "super-user/system-notifications",
      rawResponseSchema: z.array(systemNotificationResponseSchema),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: SystemNotificationResponseJSON[]) =>
        response.map((item) =>
          new SystemNotificationAPIResponse({
            ...item,
            severity: parseSystemMessageSeverity(item.severity),
          }).deserialize(),
        ),
      transformErrorResponse: (response, meta) => {
        if (getApiErrorStatus(response) === HttpStatusCode.Forbidden) return new SystemNotificationsNotFoundError();
        return toSuperUserApiError(response, meta);
      },
      providesTags: [systemNotificationsTag],
    }),
    getActiveUserInvitations: build.query<UserEntityMemberInvitation[], void>({
      query: () => "super-user/active-user-invitations",
      rawResponseSchema: z.array(UserEntityMemberInvitationSchema.UserEntityMemberInvitationJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: UserEntityMemberInvitationJSON[]) => UserEntityMemberInvitation.fromJSONArray(response),
      transformErrorResponse: (response, meta) => {
        if (getApiErrorStatus(response) === HttpStatusCode.NotFound) return new UserEntityMemberInvitationsNotFoundError();
        return toSuperUserApiError(response, meta);
      },
      providesTags: [activeInvitationsTag],
    }),
  }),
});

export const {
  useRegisterSuperUserMutation,
  useUnregisterSuperUserMutation,
  useReplaceOnboardingProposalMutation,
  useCloseOnboardingMutation,
  useDisableSuperUserMutation,
  useEnableSuperUserMutation,
  useLoginAsUserMutation,
  useCreateSystemNotificationMutation,
  useUpdateSystemNotificationMutation,
  useDeleteSystemNotificationMutation,
  useSearchUsersQuery,
  useGetAppVersionQuery,
  useGetSystemNotificationsQuery,
  useGetActiveUserInvitationsQuery,
} = superUserApi;

export class SuperUserAPIError extends Error {
  constructor(method: string | undefined, public readonly error: unknown) {
    const endpointName = method ? `.${method}` : "";
    super(`The administrator request${endpointName} could not be completed.`);
  }
}

export class SystemNotificationsNotFoundError extends Error {
  constructor() {
    super("Unable to fetch any system notifications");
  }
}

export class UserEntityMemberInvitationsNotFoundError extends Error {
  constructor() {
    super("Unable to retrieve open user entity member invitations");
  }
}

export class VersionInfoNotAvailableError extends Error {}

function toSuperUserApiError(response: FetchBaseQueryError, meta?: unknown): SuperUserAPIError {
  return new SuperUserAPIError(getApiEndpointName(meta), response);
}

function searchUsersParams(request: SearchUsersAPIRequest): URLSearchParams {
  const params = request.searchParams;
  if (request.query) params.set("query", request.query);
  if (request.sort) params.set("sort", request.sort);
  return params;
}
